import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { addDays, getDate, getTime } from 'date-fns';

import logo from '../../assets/logo.svg';
import noAvatar from '../../assets/no-avatar.svg';
import noMidia from '../../assets/no-midia.svg';

import { Icon } from '../Icon';
import { colors } from '../../styles/tokens';
import {
  Brand,
  BrandRow,
  ButtonMenuMobile,
  CollapseButton,
  CollapseButtonRow,
  Container,
  Divisor,
  GymInfo,
  GymSwitcher,
  Main,
  ModeToggle,
  ModeToggleButton,
  Nav,
  NotificationButton,
  NotificationDot,
  Overlay,
  PlatformIconBadge,
  PlatformTag,
  RightActions,
  Sidebar,
  TopBar,
  UserInfo,
} from './styles';

import { ElementMenu } from './components/ElementMenu';

import { useAuth } from '../../hooks/auth';
import { useSidebar } from '../../hooks/sidebar';
import { useToast } from '../../hooks/toast';
import { useModal } from '../../hooks/modal';
import { HelpCenter } from '../../modais/helpCenter';
import { NotificationsPanel } from '../../modais/notificationsPanel';
import { api } from '../../services/api';
import { labelLevelAccess } from '../../utils/labelLeverAccess';
import { Loading } from '../Loading';

interface IPainelProps {
  loading?: boolean;
  children: React.ReactNode;
}

const Painel: React.FC<IPainelProps> = ({ children, loading = false }) => {
  const [activeMenu, setActiveMenu] = useState(false);
  const [overlayLoading, setOverlayLoading] = useState(loading);

  const {
    user,
    signOut,
    memberships,
    activeGymId,
    switchGym,
    viewMode,
    setViewMode,
  } = useAuth();
  const { addToast } = useToast();
  const { addModal, modals } = useModal();
  const { collapsed, toggleCollapsed } = useSidebar();

  const [unreadNotifications, setUnreadNotifications] = useState(0);

  const navigate = useNavigate();

  const hasPlatformPermission = !!user?.permissions?.includes(
    'manage_platform_gyms',
  );
  const isPlatformMode = hasPlatformPermission && viewMode === 'platform';

  const handleDropMenu = useCallback(() => {
    setActiveMenu(!activeMenu);
  }, [activeMenu]);

  const handleSwitchGym = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      switchGym(e.target.value).catch(() => {
        addToast({
          type: 'error',
          title: 'Erro ao trocar de academia.',
        });
      });
    },
    [switchGym, addToast],
  );

  useEffect(() => {
    setOverlayLoading(loading);
  }, [loading]);

  const NOTIFICATIONS_POLL_INTERVAL_MS = 30000;

  const fetchUnreadNotifications = useCallback(() => {
    api
      .get('notifications', { params: { app: 'staff', limit: 1 } })
      .then(response => setUnreadNotifications(response.data.unreadCount))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    // Refaz a contagem sempre que um modal fecha (ex.: o painel de
    // notificações), mesmo padrão já usado em `warnings` pra recarregar
    // dados depois de fechar um modal de edição.
    fetchUnreadNotifications();
  }, [modals, fetchUnreadNotifications]);

  // Sem isso, o sininho só atualizava ao abrir/fechar um modal -- não
  // tinha nenhuma forma de saber que chegou notificação nova enquanto o
  // usuário só navega entre páginas. Polling simples (mesmo padrão já
  // usado no chat do mobile), não é WebSocket -- atualiza a cada 30s.
  useEffect(() => {
    const timer = setInterval(
      fetchUnreadNotifications,
      NOTIFICATIONS_POLL_INTERVAL_MS,
    );
    return () => clearInterval(timer);
  }, [fetchUnreadNotifications]);

  const planLabel = useMemo(() => {
    const maxDaysTestPeriod = 15;
    const now = getTime(new Date());

    if (user?.gym?.signature === 'test') {
      const maxTestPeriodDate = getTime(
        addDays(
          getTime(new Date(user?.gym?.created_at || now)),
          maxDaysTestPeriod,
        ),
      );

      return `Teste: expira em ${getDate(maxTestPeriodDate - now)} dias`;
    }

    return `Plano ${user?.gym?.signature?.toUpperCase() || ''}`;
  }, [user]);

  return (
    <Container>
      <ButtonMenuMobile onClick={handleDropMenu}>
        <Icon
          name={activeMenu ? 'close' : 'menu'}
          size={22}
          color={colors.onSurface}
        />
      </ButtonMenuMobile>

      <Sidebar activeMenu={activeMenu} collapsed={collapsed}>
        <Brand collapsed={collapsed}>
          <BrandRow>
            <img src={logo} alt="TrenzoX" />
            <h1>TrenzoX</h1>
          </BrandRow>
          <p>Painel Administrativo</p>
        </Brand>

        <CollapseButtonRow collapsed={collapsed}>
          <CollapseButton onClick={toggleCollapsed}>
            <Icon
              name={collapsed ? 'left_panel_open' : 'left_panel_close'}
              size={20}
              color={colors.onSurface}
            />
          </CollapseButton>
        </CollapseButtonRow>

        <Nav>
          <div>
            {isPlatformMode ? (
              <>
                <ElementMenu
                  icon="domain"
                  label="Academias"
                  path="/admin/gyms"
                  onClick={() => navigate('/admin/gyms')}
                  requiredPermission="manage_platform_gyms"
                  userPermissions={user?.permissions}
                />

                <ElementMenu
                  icon="manage_accounts"
                  label="Gestão de usuários"
                  path="/admin/users"
                  onClick={() => navigate('/admin/users')}
                  requiredPermission="manage_platform_gyms"
                  userPermissions={user?.permissions}
                />

                <ElementMenu
                  icon="fitness_center"
                  label="Exercícios padrão"
                  path="/admin/exercises"
                  onClick={() => navigate('/admin/exercises')}
                  requiredPermission="manage_platform_gyms"
                  userPermissions={user?.permissions}
                />

                <ElementMenu
                  icon="contact_support"
                  label="Central de Suporte"
                  requiredPermission="manage_platform_gyms"
                  userPermissions={user?.permissions}
                  submenu={[
                    {
                      label: 'Mensagens',
                      path: '/admin/support',
                      onClick: () => navigate('/admin/support'),
                      requiredPermission: 'manage_platform_gyms',
                    },
                    {
                      label: 'Exclusão de conta',
                      path: '/admin/support/account-deletions',
                      onClick: () =>
                        navigate('/admin/support/account-deletions'),
                      requiredPermission: 'manage_platform_gyms',
                    },
                  ]}
                />

                <ElementMenu
                  icon="workspace_premium"
                  label="Gestão de planos"
                  path="/admin/plans"
                  onClick={() => navigate('/admin/plans')}
                  requiredPermission="manage_platform_gyms"
                  userPermissions={user?.permissions}
                />

                <ElementMenu
                  icon="payments"
                  label="Assinaturas & Faturamento"
                  path="/admin/billing"
                  onClick={() => navigate('/admin/billing')}
                  requiredPermission="manage_platform_gyms"
                  userPermissions={user?.permissions}
                />

                <ElementMenu
                  icon="insights"
                  label="Relatórios globais"
                  path="/admin/reports"
                  onClick={() => navigate('/admin/reports')}
                  requiredPermission="manage_platform_gyms"
                  userPermissions={user?.permissions}
                />

                <ElementMenu
                  icon="storefront"
                  label="Gerenciamento de loja"
                  path="/admin/app-versions"
                  onClick={() => navigate('/admin/app-versions')}
                  requiredPermission="manage_platform_gyms"
                  userPermissions={user?.permissions}
                />

                <ElementMenu
                  icon="settings"
                  label="Config. do sistema"
                  path="/admin/settings"
                  onClick={() => navigate('/admin/settings')}
                  requiredPermission="manage_platform_gyms"
                  userPermissions={user?.permissions}
                />
              </>
            ) : (
              <>
                <ElementMenu
                  icon="dashboard"
                  label="Dashboard"
                  path="/dashboard"
                  onClick={() => navigate('/dashboard')}
                  requiredFeature="dashboard_overview"
                  userFeatures={user?.features}
                />

                <ElementMenu
                  icon="group"
                  label="Usuários"
                  requiredPermission="view_gym_users"
                  userPermissions={user?.permissions}
                  userFeatures={user?.features}
                  submenu={[
                    {
                      label: 'Diretório',
                      path: '/users',
                      onClick: () => navigate('/users'),
                      requiredPermission: 'view_gym_users',
                    },
                    {
                      label: 'Inadimplência',
                      path: '/users/overdue',
                      onClick: () => navigate('/users/overdue'),
                      requiredPermission: 'manage_student_billing',
                      requiredFeature: 'overdue_alert',
                    },
                    {
                      label: 'Avaliação física',
                      path: '/users/physical-assessment',
                      onClick: () => navigate('/users/physical-assessment'),
                      requiredPermission: 'manage_measurements',
                      requiredFeature: 'student_metrics',
                    },
                    {
                      label: 'Permissões',
                      path: '/users/permissions',
                      onClick: () => navigate('/users/permissions'),
                      requiredPermission: 'manage_gym_users',
                      requiredFeature: 'user_permission_management',
                    },
                  ]}
                />

                <ElementMenu
                  icon="assignment"
                  label="Fichas de treino"
                  path="/trainings"
                  excludePaths={['/trainings/templates']}
                  onClick={() => navigate('/trainings')}
                  requiredPermission="manage_trainings"
                  userPermissions={user?.permissions}
                  requiredFeature="training_management"
                  userFeatures={user?.features}
                />

                <ElementMenu
                  icon="fitness_center"
                  label="Exercicios"
                  path="/exercises"
                  requiredPermission="view_exercises"
                  userPermissions={user?.permissions}
                  onClick={() => navigate('/exercises')}
                />

                <ElementMenu
                  icon="assignment_turned_in"
                  label="Templates de treino"
                  path="/trainings/templates"
                  onClick={() => navigate('/trainings/templates')}
                  requiredPermission="manage_training_templates"
                  userPermissions={user?.permissions}
                  requiredFeature="training_templates"
                  userFeatures={user?.features}
                />

                <ElementMenu
                  icon="insights"
                  label="Relatórios"
                  path="/reports"
                  onClick={() => navigate('/reports')}
                  requiredPermission="view_gym_dashboard"
                  userPermissions={user?.permissions}
                  requiredFeature={['advanced_reports', 'at_risk_students']}
                  userFeatures={user?.features}
                />

                <ElementMenu
                  icon="campaign"
                  label="Avisos"
                  path="/warnings"
                  onClick={() => navigate('/warnings')}
                  requiredPermission="manage_news"
                  userPermissions={user?.permissions}
                  requiredFeature="announcements"
                  userFeatures={user?.features}
                />

                <ElementMenu
                  icon="receipt_long"
                  label="Totem de Impressão"
                  onClick={() =>
                    window.open('/print-kiosk', '_blank', 'noopener,noreferrer')
                  }
                  requiredPermission="manage_trainings"
                  userPermissions={user?.permissions}
                  requiredFeature="print_kiosk"
                  userFeatures={user?.features}
                />

                <ElementMenu
                  icon="chat"
                  label="Atendimento"
                  path="/support"
                  onClick={() => navigate('/support')}
                  requiredPermission="manage_chat"
                  userPermissions={user?.permissions}
                  requiredFeature="chat"
                  userFeatures={user?.features}
                />

                <ElementMenu
                  icon="storefront"
                  label="Academia"
                  requiredPermission="view_gym_dashboard"
                  userPermissions={user?.permissions}
                  userFeatures={user?.features}
                  submenu={[
                    {
                      label: 'Alterar dados',
                      path: '/gym/profile',
                      onClick: () => navigate('/gym/profile'),
                      requiredPermission: 'manage_gym_profile',
                    },
                    {
                      label: 'Planos',
                      path: '/gym/subscriptions',
                      onClick: () => navigate('/gym/subscriptions'),
                      requiredPermission: 'manage_subscriptions',
                    },
                    {
                      label: 'Pagamentos',
                      path: '/gym/payments',
                      onClick: () => navigate('/gym/payments'),
                      requiredPermission: 'manage_payments',
                    },
                  ]}
                />
              </>
            )}
          </div>

          <div>
            <Divisor />

            <ElementMenu
              icon="person"
              label="Perfil"
              path="/profile"
              onClick={() => navigate('/profile')}
            />

            <ElementMenu
              icon="support_agent"
              label="Central de ajuda"
              onClick={() => addModal(<HelpCenter />)}
            />

            <ElementMenu
              icon="logout"
              label="Sair"
              onClick={() => {
                signOut();
                navigate('/');
              }}
            />

            <PlatformTag collapsed={collapsed}>TRENZOX</PlatformTag>
          </div>
        </Nav>
      </Sidebar>

      {activeMenu && <Overlay onClick={() => setActiveMenu(false)} />}

      <TopBar collapsed={collapsed}>
        {isPlatformMode ? (
          <GymInfo>
            <PlatformIconBadge>
              <Icon
                name="admin_panel_settings"
                size={20}
                color={colors.onPrimary}
              />
            </PlatformIconBadge>
            <div>
              <h2>Painel da Plataforma</h2>
              <p>Administração</p>
            </div>
          </GymInfo>
        ) : (
          <GymInfo>
            <img src={user?.gym?.logo_url || noMidia} alt="logo academia" />
            <div>
              <h2>{user?.gym?.name}</h2>
              <p>{planLabel}</p>
            </div>
          </GymInfo>
        )}

        <RightActions>
          {hasPlatformPermission && (
            <ModeToggle>
              <ModeToggleButton
                type="button"
                active={!isPlatformMode}
                onClick={() => {
                  setViewMode('gym');
                  navigate('/dashboard');
                }}
              >
                Academia
              </ModeToggleButton>
              <ModeToggleButton
                type="button"
                active={isPlatformMode}
                onClick={() => {
                  setViewMode('platform');
                  navigate('/admin/gyms');
                }}
              >
                Administração
              </ModeToggleButton>
            </ModeToggle>
          )}

          {!isPlatformMode && memberships.length > 1 && (
            <GymSwitcher
              value={activeGymId || user?.gym_id || ''}
              onChange={handleSwitchGym}
            >
              {memberships.map(membership => (
                <option key={membership.gym_id} value={membership.gym_id}>
                  {membership.gym_name}
                </option>
              ))}
            </GymSwitcher>
          )}

          <NotificationButton
            type="button"
            onClick={() => addModal(<NotificationsPanel />)}
          >
            <Icon name="notifications" size={22} color={colors.primary} />
            {unreadNotifications > 0 && <NotificationDot />}
          </NotificationButton>

          <UserInfo type="button" onClick={() => navigate('/profile')}>
            <div>
              <h3>{user?.fullname}</h3>
              <p>{labelLevelAccess(user?.permissions)}</p>
            </div>
            <img src={user?.avatar_url || noAvatar} alt="avatar do usuário" />
          </UserInfo>
        </RightActions>
      </TopBar>

      <Main collapsed={collapsed}>
        {overlayLoading && <Loading />}

        {children}
      </Main>
    </Container>
  );
};

export { Painel };
